import { useCallback, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material"
import type { SaveLayoutTier } from "resources/layouts"
import { useDialogFormState } from "../../hooks/use-dialog-form-state"

export interface SaveAsNewDialogProps {
  open: boolean
  onClose: () => void
  // Resolves on success (dialog closes); rejects to keep the dialog open with the inline error.
  onSave: (name: string, tier: SaveLayoutTier) => Promise<void>
  // Offers "Everyone on this account"; off by default, gated by the caller. (APP-2690)
  canShareWithAccount?: boolean
}

export default function SaveAsNewDialog({ open, onClose, onSave, canShareWithAccount = false }: SaveAsNewDialogProps) {
  const [name, setName] = useState("")
  const [tier, setTier] = useState<SaveLayoutTier>("user")
  // Clamp to "user" if canShareWithAccount is false, in case stale radio state or a mid-dialog
  // account switch would otherwise reach onSave. (APP-2690)
  const effectiveTier: SaveLayoutTier = canShareWithAccount ? tier : "user"
  const {
    isBusy: isSaving,
    error,
    beginBusy,
    endBusy,
    reset: resetFormState,
    guardedClose,
  } = useDialogFormState()

  const reset = useCallback(() => {
    setName("")
    setTier("user")
    resetFormState()
  }, [resetFormState])

  const handleClose = useCallback(() => {
    guardedClose(() => { reset(); onClose() })
  }, [guardedClose, reset, onClose])

  const trimmedName = name.trim()
  const isSaveDisabled = trimmedName.length === 0 || isSaving

  const handleSave = useCallback(async () => {
    if (trimmedName.length === 0) return
    if (!beginBusy()) return
    try {
      await onSave(trimmedName, effectiveTier)
      reset()
      onClose()
    } catch {
      // Keep the dialog open so the user can retry without re-typing the name or tier. [APP-2690]
      endBusy("Couldn't save the layout. Please try again.")
    }
  }, [trimmedName, effectiveTier, beginBusy, onSave, reset, onClose, endBusy])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Save as new layout</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Layout name"
          value={name}
          onChange={(event) => { setName(event.target.value) }}
          error={error !== null}
          helperText={error ?? undefined}
          disabled={isSaving}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void handleSave()
            }
          }}
        />
        {canShareWithAccount && (
          <FormControl sx={{ mt: 1.5 }} disabled={isSaving}>
            <FormLabel id="save-as-new-tier-label">Who can use this layout</FormLabel>
            <RadioGroup
              aria-labelledby="save-as-new-tier-label"
              value={effectiveTier}
              onChange={(event) => { setTier(event.target.value as SaveLayoutTier) }}
            >
              <FormControlLabel value="user" control={<Radio size="small" />} label="Just me" />
              <FormControlLabel value="account" control={<Radio size="small" />} label="Everyone on this account" />
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
        <Button onClick={() => { void handleSave() }} disabled={isSaveDisabled} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
